import { useCallback, useMemo, useState } from 'react'
import { SelectedOrgChecker } from '@features/organisations/components/SelectedOrgChecker'
import { createFileRoute, redirect, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { z } from 'zod'
import { EmptyState } from '@features/multiview/components/EmptyState'
import { Multiview } from '@features/multiview/components/Multiview'
import { SessionSelector } from '@features/multiview/components/SessionSelector'
import { MultiviewErrorBoundary } from '@features/multiview/components/MultiviewErrorBoundary'
import {
  parseMultiviewState,
  loadFromSessionStorage,
  saveToSessionStorage,
  encodeMultiviewState,
  MultiviewPersistedState,
} from '@features/multiview/utils/persistence'
import { MultiviewFeedId } from '@features/multiview/utils/feedId'

const multiviewSearchSchema = z.object({
  feeds: z.string().optional(),
}).partial().catch({})

const MultiviewContent = () => {
  const { orgId } = useParams({ strict: false })
  const navigate = useNavigate()
  const search = useSearch({ from: Route.id })

  const { feeds, colors } = useMemo(
    () => parseMultiviewState(search.feeds),
    [search.feeds]
  )

  const persistState = useCallback(
    (state: MultiviewPersistedState) => {
      if (!orgId) return
      saveToSessionStorage(orgId, state)
      const encoded = encodeMultiviewState(state)
      navigate({
        to: '.',
        search: (prev) => {
          if (encoded === '') {
            const { feeds: _, ...rest } = prev as { feeds?: string }
            return rest
          }
          return { ...prev, feeds: encoded }
        },
        replace: true,
      })
    },
    [orgId, navigate]
  )

  const setFeeds = useCallback(
    (newFeeds: MultiviewFeedId[]) => {
      persistState({ feeds: newFeeds, colors })
    },
    [persistState, colors]
  )

  const setColors = useCallback(
    (newColors: Record<string, number>) => {
      persistState({ feeds, colors: newColors })
    },
    [persistState, feeds]
  )

  const [isSessionSelectorOpen, setIsSessionSelectorOpen] = useState(false)

  const handleAddSessions = useCallback(() => {
    setIsSessionSelectorOpen(true)
  }, [])

  const handleCloseSessionSelector = useCallback(() => {
    setIsSessionSelectorOpen(false)
  }, [])

  return (
    <>
      {feeds.length === 0 ? (
        <EmptyState onAddSessions={handleAddSessions} />
      ) : (
        <Multiview
          feeds={feeds}
          onFeedsChange={setFeeds}
          colors={colors}
          onColorsChange={setColors}
        />
      )}

      <SessionSelector
        isOpen={isSessionSelectorOpen}
        onClose={handleCloseSessionSelector}
        feeds={feeds}
        onFeedsChange={setFeeds}
      />
    </>
  )
}

const MultiviewRoute = () => {
  return (
    <SelectedOrgChecker>
      <MultiviewErrorBoundary>
        <MultiviewContent />
      </MultiviewErrorBoundary>
    </SelectedOrgChecker>
  )
}

export const Route = createFileRoute('/_auth/o/$orgId/multiview/')({
  component: MultiviewRoute,
  validateSearch: zodValidator(multiviewSearchSchema),
  beforeLoad: ({ search, params }) => {
    if (!search.feeds) {
      const stored = loadFromSessionStorage(params.orgId)
      if (stored.feeds.length > 0) {
        throw redirect({
          to: '/o/$orgId/multiview',
          params: { orgId: params.orgId },
          search: { feeds: encodeMultiviewState(stored) },
          replace: true,
        })
      }
    }
    return { getTitle: () => 'Multiview' }
  },
})
